import React from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Popconfirm, Select, Tooltip } from 'antd';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ApplicationState } from '../../reducers';
import {
  deleteAPIToken,
  fetchAPIToken,
  fetchAccountAPITokens,
  updateSelectedAPIToken,
} from '../../actions/distribution_apisettings';
import ApiSettingsPage from '../../components/body/distribution/ApiSettingsPage';
import ApiSettingsAddNewTokenPopover from '../../components/body/distribution/ApiSettingsAddNewTokenPopover';
import { getPageLimit } from '../../utils/Utils';
import { intercomEvent } from '../../utils/IntercomUtils';
import { hasPermission } from '../../utils/Permissions';
import { Token } from '../../../types/distribution';

const { Paragraph } = Typography;

const APISettingsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const hasFetchedRef = React.useRef<number | null>(null);

  const {
    apiSettings,
    user,
    fetchingLogsAndToken,
    accountAPITokens,
    selectedTokenId,
    fetchingAccountAPITokens,
  } = useSelector((state: ApplicationState) => {
    return {
      apiSettings: state.distribution.apiSettings,
      user: state.user.user,
      fetchingLogsAndToken: state.distribution.apiSettings.fetchingLogsAndToken,
      accountAPITokens: state.distribution.apiSettings.accountAPITokens,
      selectedTokenId: state.distribution.apiSettings.selectedTokenId,
      fetchingAccountAPITokens: state.distribution.apiSettings.fetchingAccountAPITokens,
    };
  }, shallowEqual);

  const selectedToken = React.useMemo(() => {
    if (selectedTokenId && Array.isArray(accountAPITokens)) {
      return accountAPITokens.find(token => token.id === selectedTokenId);
    }
    return apiSettings?.token;
  }, [selectedTokenId, accountAPITokens, apiSettings?.token]);

  const handleTokenChange = (value: number) => {
    if (value) {
      dispatch(updateSelectedAPIToken(value));
    }
  };

  const allAccountTokens = React.useMemo(() => {
    if (fetchingAccountAPITokens || !Array.isArray(accountAPITokens)) {
      if (!apiSettings?.token?.id) return [];
      const token = apiSettings.token;
      let optionDesc = token.description;
      if (token.created_by) {
        optionDesc = `${optionDesc} - ${t('api:tokenUser')} ${token.created_by}`;
      }
      return [
        {
          value: token.id,
          label: `${token.description}: ${token.token}`,
          desc: optionDesc,
        },
      ];
    }

    return accountAPITokens.map(token => {
      let optionDesc = token.description;
      if (token.created_by) {
        optionDesc = `${optionDesc} - ${t('api:tokenUser')} ${token.created_by}`;
      }
      return {
        value: token.id,
        label: `${token.description}: ${token.token}`,
        desc: optionDesc,
      };
    });
  }, [apiSettings, accountAPITokens, fetchingAccountAPITokens, t]);

  const renderToken = () => {
    if (fetchingLogsAndToken || fetchingAccountAPITokens) return <LoadingOutlined />;

    if (accountAPITokens && accountAPITokens.length > 1) {
      return (
        <Select
          options={allAccountTokens}
          value={selectedTokenId || apiSettings?.token?.id}
          onChange={handleTokenChange}
          className="api-settings__token-select"
          labelRender={option => {
            const token = allAccountTokens.find(token => token.value === option.value);
            return <Tooltip title={token?.desc}>{option.label}</Tooltip>;
          }}
          optionRender={option => <Tooltip title={option.data.desc}>{option.label}</Tooltip>}
        />
      );
    }

    const token: Token | null = apiSettings?.token;
    if (fetchingLogsAndToken) return <LoadingOutlined />;
    if (token.token) {
      return (
        <Tooltip title={selectedToken?.description}>
          <span>{token.token}</span>
        </Tooltip>
      );
    }
    return t('api:token');
  };

  React.useEffect(() => {
    intercomEvent('viewed-api', { location: 'settings' });
  }, []);

  React.useEffect(() => {
    dispatch(fetchAccountAPITokens());
  }, [dispatch]);

  React.useEffect(() => {
    if (!fetchingAccountAPITokens && accountAPITokens?.length > 0 && selectedTokenId > 0) {
      if (hasFetchedRef.current !== selectedTokenId) {
        hasFetchedRef.current = selectedTokenId;
        dispatch(fetchAPIToken({ tokenId: selectedTokenId, limit: getPageLimit() }));
      }
    }
  }, [dispatch, fetchingAccountAPITokens, accountAPITokens, selectedTokenId]);

  const canCreateApiToken = hasPermission(user, 'can_create_api_token');

  const deleteToken = async () => {
    if (accountAPITokens.length <= 1) return;
    await dispatch(deleteAPIToken(selectedTokenId));
    await dispatch(fetchAccountAPITokens());

    const currAccountAPITokens = accountAPITokens.filter(token => token.id !== selectedTokenId);
    if (currAccountAPITokens.length > 0) {
      dispatch(updateSelectedAPIToken(currAccountAPITokens[0].id));
    }
  };

  return (
    <div className="page-layout api-settings-container">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <div className="api-settings__alert">
            <ApiSettingsAddNewTokenPopover
              canCreateApiToken={canCreateApiToken}
              fetchingLogsAndToken={fetchingLogsAndToken}
              fetchingAccountAPITokens={fetchingAccountAPITokens}
            />
          </div>
          <div className="ml-2 w-full bg-white">
            <Paragraph
              copyable={
                selectedToken?.token && !fetchingLogsAndToken && !fetchingAccountAPITokens
                  ? { text: selectedToken.token }
                  : false
              }
              disabled={!selectedToken?.token}
              className="api-settings__token flex items-center w-full px-2 justify-between"
            >
              {renderToken()}
            </Paragraph>
          </div>

          <div className="api-settings__alert">
            {selectedToken?.token && canCreateApiToken ? (
              <Popconfirm
                title={<div>{t('api:deleteApiTokenAlertText')}</div>}
                onConfirm={() => deleteToken()}
                okText={t('common:yes')}
                cancelText={t('common:no')}
                disabled={(accountAPITokens?.length ?? 0) <= 1}
              >
                <Button
                  type="primary"
                  className="ml-2"
                  danger
                  ghost
                  disabled={
                    fetchingLogsAndToken ||
                    fetchingAccountAPITokens ||
                    (accountAPITokens?.length ?? 0) <= 1
                  }
                >
                  {t('api:deleteApiToken')}
                </Button>
              </Popconfirm>
            ) : null}
          </div>
        </div>
      </div>
      <ApiSettingsPage />
    </div>
  );
};

export default APISettingsContainer;
